import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { getOrCreateProfile, Profile, getUserPreferences, upsertUserPreferences } from './civicbeatData';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Load or create profile for a given user
  const loadProfile = async (userId: string, userEmail?: string) => {
    try {
      const username = userEmail?.split('@')[0] || null;
      const loadedProfile = await getOrCreateProfile(userId, username || undefined);

      if (loadedProfile) {
        // Check if user is disabled or deleted and sign them out
        if (loadedProfile.is_disabled || loadedProfile.deleted_at) {
          console.log('[AuthContext] User is disabled or deleted, signing out');
          await supabase.auth.signOut();
          setUser(null);
          setProfile(null);
          return;
        }
        setProfile(loadedProfile);
      }
    } catch (error) {
      console.error('[AuthContext] Error loading profile:', error);
      // Don't throw - let the user stay signed in even if profile loading fails
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { data: { user: currentUser }, error } = await supabase.auth.getUser();

        console.log('[AuthContext] Current user:', currentUser?.id, currentUser?.email);

        if (mounted) {
          setUser(currentUser);

          if (currentUser) {
            await loadProfile(currentUser.id, currentUser.email);
          }

          setLoading(false);
        }
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;

      (async () => {
        try {
          const newUser = session?.user || null;
          setUser(newUser);

          if (newUser) {
            await loadProfile(newUser.id, newUser.email);
          } else {
            setProfile(null);
          }
        } catch (error) {
          console.error('[AuthContext] Error in auth state change handler:', error);
        }
      })();
    });

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[AuthContext] Sign in error:', error.message);
        return { success: false, error: error.message };
      }

      // Check if user is disabled or deleted
      if (data.user) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_disabled, deleted_at')
          .eq('id', data.user.id)
          .maybeSingle();

        console.log('[AuthContext] Profile check:', { profileData, profileError });

        if (profileError) {
          console.error('[AuthContext] Error checking profile status:', profileError);
        }

        if (profileData) {
          if (profileData.is_disabled) {
            console.log('[AuthContext] User is disabled, signing out');
            await supabase.auth.signOut();
            return { success: false, error: 'This account has been deactivated. Please contact support.' };
          }
          if (profileData.deleted_at) {
            console.log('[AuthContext] User is deleted, signing out');
            await supabase.auth.signOut();
            return { success: false, error: 'This account has been deleted.' };
          }
        }
      }

      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Unexpected sign in error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const signUp = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        console.error('[AuthContext] Sign up error:', error.message);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Unexpected sign up error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      // Clear verification flag so it can show for next user
      sessionStorage.removeItem('civicbeat_email_verified_shown');
    } catch (error) {
      console.error('[AuthContext] Error signing out:', error);
    }
  };

  const refreshProfile = async (): Promise<void> => {
    if (!user) return;
    await loadProfile(user.id, user.email);
  };

  const value: AuthContextType = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut: handleSignOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
